import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { javascript } from "@codemirror/lang-javascript";
import { search } from "@codemirror/search";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { EditorView } from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { jsonataLanguage } from "@/lib/codemirror-jsonata";
import { x12Language } from "@/lib/codemirror-x12";

const heightTheme = EditorView.theme({
  "&": { height: "100%" },
  ".cm-scroller": { overflow: "auto" },
});

const searchExtension = search({
  top: true,
  scrollToMatch: (range) => EditorView.scrollIntoView(range, { yMargin: 50 }),
});

// Colors derived from the app's Tailwind theme palette. Every value clears
// 4.5:1 against both the editor background and the active-line highlight.
const highlightStyle = syntaxHighlighting(
  HighlightStyle.define([
    { tag: t.string, color: "#115EA3" }, // blue (from --accent-foreground)
    { tag: t.special(t.string), color: "#087966" }, // teal (backtick ids, regex)
    { tag: t.special(t.variableName), color: "#1A7D2A" }, // green (from --affirmative, $variables)
    { tag: t.keyword, color: "#BD5205" }, // rust (from --link)
    { tag: t.atom, color: "#BD5205" }, // rust (true/false/null)
    { tag: t.number, color: "#723C17" }, // brown (from --card-foreground)
    { tag: t.comment, color: "#746C63", fontStyle: "italic" }, // gray (from --muted-foreground)
    { tag: t.operator, color: "#723C17" }, // brown
    { tag: t.punctuation, color: "#746C63" }, // muted gray
    { tag: t.variableName, color: "#2C3E50" }, // dark (identifiers)
    { tag: t.propertyName, color: "#2C3E50" }, // dark (JSON keys)
  ]),
);

interface JsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  readonly?: boolean;
  language?: string;
  theme?: string;
  lineWrapping?: boolean;
  height?: string;
  /** Accessible name for the editor. CodeMirror renders role="textbox" on a
      contenteditable div, which has no label of its own. */
  label?: string;
}

export interface JsonEditorHandle {
  // Scroll to and place the cursor on a 1-based line number.
  scrollToLine: (line: number) => void;
}

const JsonEditor = forwardRef<JsonEditorHandle, JsonEditorProps>(
  function JsonEditor(
    {
      value,
      onChange,
      readonly = false,
      language = "json",
      lineWrapping = false,
      height = "65vh",
      label,
    },
    ref,
  ) {
    const cmRef = useRef<ReactCodeMirrorRef>(null);

    useImperativeHandle(ref, () => ({
      scrollToLine: (line: number) => {
        const view = cmRef.current?.view;
        if (!view) return;
        const target = Math.max(1, Math.min(line, view.state.doc.lines));
        const lineObj = view.state.doc.line(target);
        view.dispatch({
          selection: { anchor: lineObj.from },
          effects: EditorView.scrollIntoView(lineObj.from, { y: "center" }),
        });
        view.focus();
      },
    }));

    const extensions = useMemo(() => {
      const langExtension = (() => {
        switch (language) {
          case "json":
            return json();
          case "javascript":
            return javascript({ jsx: true });
          case "jsonata":
            return jsonataLanguage;
          case "edi":
            return x12Language;
          case "text":
            return [];
          default:
            return json();
        }
      })();
      const exts = [
        langExtension,
        searchExtension,
        highlightStyle,
        heightTheme,
      ];
      if (lineWrapping) exts.push(EditorView.lineWrapping);
      exts.push(
        EditorView.contentAttributes.of({
          "aria-label": label ?? `${language.toUpperCase()} editor`,
        }),
      );
      return exts;
    }, [language, lineWrapping, label]);

    return (
      <div
        className="border border-border w-full min-w-0 overflow-hidden flex flex-col"
        style={{ height }}
      >
        <CodeMirror
          ref={cmRef}
          value={value}
          extensions={extensions}
          onChange={onChange}
          readOnly={readonly}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: !readonly,
          }}
          className="flex-1 min-h-0 overflow-hidden [&_.cm-editor]:h-full!"
        />
      </div>
    );
  },
);

export default JsonEditor;
